import React, { forwardRef, useEffect, useState } from "react";

import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import { TextField } from "js/Components/TextField";
import { Button } from "js/Components/Button";
import { ImageStyles, AiImageStyleTypes, AiImageStyles } from "common/aiConstants";
import { ShowDialog, BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";

import CustomImageStyleDialog from "./CustomImageStyleDialog";
import { ImageStyleOption } from "../components/ImageStyleInput";
import { InputContainer } from "../components/PaneComponents";

function RegenerateImageDialog(props: {
    theme: any;
    originalPromptSubject: string;
    originalImageStylePrompt: string;
    originalImageStyle: ImageStyles;
    hideImageStyle?: boolean;
    handleRegenerateImage: (promptSubject: string, imageStyle: ImageStyles, imageStylePrompt: string) => void;
    closeDialog: () => void;
}, _ref: React.Ref<unknown>) {
    const { theme, originalPromptSubject, originalImageStylePrompt, originalImageStyle, hideImageStyle = false, handleRegenerateImage, closeDialog } = props;

    const [promptSubject, setPromptSubject] = useState<string>(originalPromptSubject);
    const [imageStyle, setImageStyle] = useState<ImageStyles>(originalImageStyle);
    const [imageStylePrompt, setImageStylePrompt] = useState<string>(originalImageStylePrompt);

    useEffect(() => {
        if (imageStyle === "theme") {
            setImageStylePrompt(theme.get("imageStylePrompt") ?? AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt);
        }
    }, [theme]);

    const handleImageStyleChange = async (imageStyle: ImageStyles) => {
        setImageStyle(imageStyle);

        if (imageStyle === "custom") {
            ShowDialog(CustomImageStyleDialog, {
                theme,
                imageStyle,
                imageStylePrompt,
                hideThemeImageStyle: true,
                onSuccess: (_imageStyle: ImageStyles, imageStylePrompt: string) => {
                    setImageStylePrompt(imageStylePrompt);
                }
            });
        } else if (imageStyle === "theme") {
            setImageStylePrompt(theme.get("imageStylePrompt") ?? AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt);
        } else {
            setImageStylePrompt(AiImageStyles[imageStyle].prompt);
        }
    };

    return (
        <BeautifulDialog
            PaperProps={{ style: { maxWidth: 850 } }}
            closeDialog={closeDialog}
        >
            <FlexLayout padding="40px 50px" gap={30} direction="column">
                <TextContent header>Regenerate Image</TextContent>

                <FlexLayout gap={5} direction="column">
                    <TextContent popupDescription>Prompt</TextContent>
                    <InputContainer height={150}>
                        <TextField
                            autoFocus multiline
                            value={promptSubject}
                            placeholder={"Describe the image..."}
                            InputProps={{ disableUnderline: true }}
                            onChange={setPromptSubject}
                            onEnterKeyPress={() => {
                                handleRegenerateImage(promptSubject, imageStyle, imageStylePrompt);
                                closeDialog();
                            }}
                        />
                    </InputContainer>
                </FlexLayout>

                {!hideImageStyle &&
                    <FlexLayout gap={15} direction="column">
                        <TextContent popupDescription>Image Style</TextContent>
                        <FlexLayout gap={30} wrap="wrap">
                            {["theme", ...Object.values(AiImageStyleTypes), "custom"].map((style: ImageStyles) => (
                                <ImageStyleOption key={style} imageStyle={style} selectedImageStyle={imageStyle} onClick={handleImageStyleChange} />
                            ))}
                        </FlexLayout>
                    </FlexLayout>
                }

                <FlexLayout gap={10} justify="flex-end">
                    <Button unframed onClick={closeDialog}>Cancel</Button>
                    <Button blue onClick={() => {
                        handleRegenerateImage(promptSubject, imageStyle, imageStylePrompt);
                        closeDialog();
                    }} disabled={promptSubject.trim().length === 0}>Regenerate Image</Button>
                </FlexLayout>
            </FlexLayout>
        </BeautifulDialog>
    );
}

export default forwardRef(RegenerateImageDialog);
